/**
 * Search bar with autocomplete — Task 34.3
 */
import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import { useAutocomplete } from '@/hooks/useProducts'
import { debounce } from '@/lib/utils'

interface SearchBarProps {
  /** Sync input when URL or parent query changes (e.g. on /search page). */
  defaultQuery?: string
  /** When set, submit updates search via callback instead of navigating away. */
  onSearch?: (query: string) => void
  className?: string
}

export function SearchBar({ defaultQuery = '', onSearch, className }: SearchBarProps = {}) {
  const navigate = useNavigate()
  const [query, setQuery] = useState(defaultQuery)
  const [debouncedQuery, setDebouncedQuery] = useState(defaultQuery)
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setQuery(defaultQuery)
    setDebouncedQuery(defaultQuery)
    if (defaultQuery.length < 2) {
      setOpen(false)
    }
  }, [defaultQuery])

  const debouncedSet = useRef(
    debounce((q: unknown) => setDebouncedQuery(q as string), 300),
  ).current

  const { data: suggestions } = useAutocomplete(debouncedQuery)

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value
    setQuery(val)
    debouncedSet(val)
    setOpen(val.length >= 2)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = query.trim()
    if (!trimmed) return
    if (onSearch) {
      onSearch(trimmed)
    } else {
      navigate(`/search?q=${encodeURIComponent(trimmed)}`)
    }
    setOpen(false)
  }

  function handleSuggestionClick(slug: string) {
    navigate(`/products/${slug}`)
    setOpen(false)
    setQuery('')
  }

  // Close on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div ref={containerRef} className={className ?? 'relative w-full'}>
      <form onSubmit={handleSubmit} role="search">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={query}
            onChange={handleChange}
            onFocus={() => query.length >= 2 && setOpen(true)}
            placeholder="Search products…"
            aria-label="Search products"
            aria-autocomplete="list"
            aria-expanded={open}
            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
      </form>

      {/* Autocomplete dropdown */}
      {open && suggestions && suggestions.length > 0 && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg"
        >
          {suggestions.map((product) => (
            <li key={product.id} role="option" aria-selected={false}>
              <button
                type="button"
                onClick={() => handleSuggestionClick(product.slug)}
                className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-gray-50"
              >
                {product.images[0] && (
                  <img
                    src={product.images[0].url}
                    alt=""
                    className="h-8 w-8 rounded object-cover"
                  />
                )}
                <span className="flex-1 truncate text-gray-700">{product.name}</span>
                <span className="text-gray-500">${product.base_price.toFixed(2)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
